import React, { useState, useEffect } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from 'recharts';
import { ChevronDown, Target, TrendingUp, AlertTriangle, ShieldOff } from 'lucide-react';
import { FmiReviewCard } from '@/components/FmiReviewCard';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A', saffron: '#FF9933' };

const SCALE_COLORS = { 0: '#C7CFD9', 1: '#E24B4A', 2: '#E8A020', 3: '#1A4C7C', 4: '#1D9E75' };

function pctColor(p) {
  if (p >= 80) return C.green;
  if (p >= 60) return C.blue;
  if (p >= 40) return C.amber;
  return C.red;
}

export default function MaturityIndexPage() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    Promise.all([api.get('/fmi/benchmark'), api.get('/fmi/history')])
      .then(([b, h]) => { setData(b.data); setHistory(h.data?.snapshots || []); })
      .catch(err => console.error('FMI error:', err?.message))
      .finally(() => setLoading(false));
  }, [api]);

  if (loading) return <div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-20 rounded-md" />)}</div>;
  if (!data) return <div className="text-sm" style={{ color: C.slate }}>Unable to load the Maturity Index.</div>;

  const total = data.total;
  const target = data.target_total;
  const totalPct = data.total_pct;

  return (
    <div className="space-y-5" data-testid="maturity-index-page">
      {/* Hero */}
      <Card className="p-6 rounded-lg" style={{ background: 'linear-gradient(135deg, #0B1E3D 0%, #15375E 100%)', border: 'none' }} data-testid="fmi-hero">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] font-semibold" style={{ color: C.saffron }}>frAIme Maturity Index · FMI-108</p>
            <div className="flex items-end gap-3 mt-2">
              <span className="text-6xl font-bold font-mono" style={{ color: 'white' }} data-testid="fmi-total-score">{total}</span>
              <span className="text-xl font-medium mb-1" style={{ color: 'rgba(255,255,255,0.5)' }}>/ {data.total_max}</span>
            </div>
            <p className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.8)' }}>
              <span className="px-2 py-0.5 rounded text-xs font-semibold mr-2" style={{ background: 'rgba(255,153,51,0.18)', color: C.saffron }}>{data.band.name}</span>
              {data.band.description}
            </p>
          </div>
          <div className="md:text-right">
            <div className="flex items-center gap-2 md:justify-end">
              <Target className="w-4 h-4" style={{ color: C.teal }} />
              <span className="text-xs uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.6)' }}>Target</span>
              <span className="text-2xl font-bold font-mono" style={{ color: C.teal }}>{target}</span>
            </div>
            <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.6)' }}>{(target - total).toFixed(0)} points to go · {totalPct}% complete</p>
          </div>
        </div>
        {/* Progress bar with target marker */}
        <div className="mt-5 relative h-2.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.12)' }}>
          <div className="absolute top-0 left-0 h-full rounded-full" style={{ width: `${totalPct}%`, background: 'linear-gradient(90deg, #00B4D8, #1D9E75)' }} />
          <div className="absolute top-[-3px] h-[16px] w-[2px]" style={{ left: `${(target / data.total_max) * 100}%`, background: C.saffron }} title={`Target ${target}`} />
        </div>
      </Card>

      {/* Score history trend */}
      {history.length > 0 && (
        <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.14)' }} data-testid="fmi-trend">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" style={{ color: C.green }} />
              <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Score History — Path to {target}</h3>
            </div>
            {history.length > 1 && (
              <span className="text-xs font-semibold px-2 py-0.5 rounded" style={{ background: C.green + '14', color: C.green }}>
                {(() => { const d = total - history[history.length - 2].total; return `${d >= 0 ? '+' : ''}${d.toFixed(1)} since ${history[history.length - 2].date}`; })()}
              </span>
            )}
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={history.map(s => ({ date: s.date, score: s.total, label: s.label }))} margin={{ top: 8, right: 16, bottom: 0, left: -8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF1F5" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: C.slate }} />
              <YAxis domain={[0, data.total_max]} tick={{ fontSize: 11, fill: C.slate }} width={44} />
              <Tooltip formatter={(v) => [`${v} / ${data.total_max}`, 'FMI score']} />
              <ReferenceLine y={target} stroke={C.saffron} strokeDasharray="4 4" label={{ value: `Target ${target}`, fontSize: 10, fill: C.saffron, position: 'insideTopRight' }} />
              <Line type="monotone" dataKey="score" stroke={C.teal} strokeWidth={2.5} dot={{ r: 4, fill: C.blue }} activeDot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Category grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="fmi-categories">
        {data.categories.map(cat => {
          const isOpen = expanded === cat.id;
          const targetPct = (cat.target / cat.weight) * 100;
          return (
            <Card key={cat.id} className="rounded-md overflow-hidden" style={{ border: '1px solid rgba(136,150,168,0.14)' }} data-testid={`fmi-cat-${cat.id}`}>
              <button onClick={() => setExpanded(isOpen ? null : cat.id)} className="w-full text-left p-4 hover:bg-[#FAFBFC] transition-colors">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold" style={{ color: C.navy }}>{cat.name}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-mono font-semibold" style={{ color: pctColor(cat.pct) }}>{cat.score}</span>
                    <span className="text-xs font-mono" style={{ color: C.slate }}>/ {cat.weight}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} style={{ color: C.slate }} />
                  </div>
                </div>
                <div className="mt-2.5 relative h-2 rounded-full overflow-hidden" style={{ background: '#EEF1F5' }}>
                  <div className="absolute top-0 left-0 h-full rounded-full" style={{ width: `${cat.pct}%`, background: pctColor(cat.pct) }} />
                  <div className="absolute top-[-2px] h-[12px] w-[2px]" style={{ left: `${targetPct}%`, background: C.saffron }} />
                </div>
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-[11px]" style={{ color: C.slate }}>
                    {cat.pct}% · weight {cat.weight}{cat.held_out > 0 ? ` (${cat.held_out} held out)` : ''}
                  </span>
                  <span className="text-[11px] font-medium" style={{ color: C.amber }}>+{cat.gap} to target</span>
                </div>
              </button>
              {isOpen && (
                <div className="px-4 pb-4 pt-1 border-t" style={{ borderColor: 'rgba(136,150,168,0.1)' }} data-testid={`fmi-criteria-${cat.id}`}>
                  {cat.criteria.map(cr => (
                    <div key={cr.id} className="flex items-start gap-3 py-2 border-b last:border-0" style={{ borderColor: 'rgba(136,150,168,0.06)' }}>
                      <span className="shrink-0 w-6 h-6 rounded flex items-center justify-center text-[11px] font-bold font-mono mt-0.5"
                        style={{ background: (cr.out_of_scope ? C.slate : SCALE_COLORS[cr.score]) + '20', color: cr.out_of_scope ? C.slate : SCALE_COLORS[cr.score] }}>
                        {cr.out_of_scope ? '—' : cr.score}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs font-medium" style={{ color: C.navy }}><span className="font-mono mr-1.5" style={{ color: C.slate }}>{cr.id}</span>{cr.name}</span>
                          <span className="text-[10px] uppercase tracking-wider shrink-0" style={{ color: cr.out_of_scope ? C.slate : SCALE_COLORS[cr.score] }}>{cr.out_of_scope ? 'Out of scope' : cr.label}</span>
                        </div>
                        <p className="text-[11px] mt-0.5" style={{ color: C.slate }}>{cr.note}</p>
                        {cr.out_of_scope && (
                          <p className="text-[11px] mt-1 pl-2 border-l-2" style={{ color: C.slate, borderColor: C.saffron }} data-testid={`fmi-out-of-scope-${cr.id}`}>{cr.reason}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Latest re-grade — what moved, what was held, findings from testing */}
      <FmiReviewCard review={data.reviews?.[0]} categories={data.categories} total={total} totalMax={data.total_max} />

      {/* Out of scope by the owner's ruling — what is deliberately not being worked on */}
      {data.owner_rulings?.length > 0 && (
        <Card className="rounded-md overflow-hidden" style={{ border: '1px solid rgba(136,150,168,0.14)' }} data-testid="fmi-owner-rulings">
          <div className="p-4 flex flex-wrap items-center gap-2 border-b" style={{ borderColor: 'rgba(136,150,168,0.1)' }}>
            <ShieldOff className="w-4 h-4" style={{ color: C.slate }} />
            <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Out of Scope — the Owner's Ruling</h3>
            <span className="ml-auto text-[11px] font-mono" style={{ color: C.slate }} data-testid="fmi-points-held-out">
              {data.points_held_out} pts held out · scored out of {data.total_max}
            </span>
          </div>
          <div>
            {data.owner_rulings.map((r, i) => (
              <div key={i} className="p-4 border-b last:border-0" style={{ borderColor: 'rgba(136,150,168,0.08)' }} data-testid={`fmi-ruling-${i}`}>
                <p className="text-xs font-semibold" style={{ color: C.navy }}>{r.topic}</p>
                <p className="text-[11px] mt-1" style={{ color: C.slate }}>{r.ruling}</p>
                <p className="text-[11px] mt-1.5 pl-2 border-l-2" style={{ color: C.slate, borderColor: C.saffron }}>{r.index_effect}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Priorities — where the points leak */}
      <Card className="rounded-md overflow-hidden" style={{ border: '1px solid rgba(136,150,168,0.14)' }} data-testid="fmi-priorities">
        <div className="p-4 flex items-center gap-2 border-b" style={{ borderColor: 'rgba(136,150,168,0.1)' }}>
          <TrendingUp className="w-4 h-4" style={{ color: C.saffron }} />
          <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Highest-Leverage Gaps (weight × maturity gap)</h3>
        </div>
        <table className="w-full text-xs">
          <thead>
            <tr style={{ background: '#FAFBFC' }} className="border-b">
              {['#', 'Criterion', 'Category', 'Score', 'Weighted Gap'].map(h => <th key={h} className="text-left py-2 px-3 text-[10px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.priorities.map((p, i) => (
              <tr key={p.id} className="border-b" style={{ borderColor: 'rgba(136,150,168,0.06)' }}>
                <td className="py-2 px-3 font-mono" style={{ color: C.slate }}>{i + 1}</td>
                <td className="py-2 px-3"><span className="font-mono mr-1.5" style={{ color: C.slate }}>{p.id}</span><span className="font-medium" style={{ color: C.navy }}>{p.name}</span></td>
                <td className="py-2 px-3" style={{ color: C.slate }}>{p.category}</td>
                <td className="py-2 px-3"><span className="px-1.5 py-0.5 rounded text-[10px] font-bold font-mono" style={{ background: SCALE_COLORS[p.score] + '20', color: SCALE_COLORS[p.score] }}>{p.score}/4</span></td>
                <td className="py-2 px-3 font-mono font-semibold" style={{ color: C.red }}>+{p.weighted_gap}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <p className="text-[11px] flex items-center gap-1.5" style={{ color: C.slate }}>
        <AlertTriangle className="w-3 h-3" /> FMI-108 v{data.version} · re-graded each release · {data.scale_note} · source rubric in /app/memory/FMI_108_Benchmark.md
      </p>
    </div>
  );
}
